import React, { useContext, useEffect, useState } from 'react'
import { SnackBarContext } from '../../Components/SnackBar'
import CreativeHeader from './CreativeHeader'
import FilterSection from './FilterSection'
import DesignTable from './DesignTable'
import DesignPreviewModal from './DesignPreviewModal/DesignPreviewModal'
import Pagination from './Pagination'
import useDesignManagement, { DESIGN_STATUS } from './hooks/useDesignManagement'
import { openHtmlInNewTabAndPrint } from './util/printHTML'
import { authorization } from '../../Module/Auth/Auth'
import { addAuthParamsToCampaignBuilderUrl } from '../Country/Page/Pipeline/PipelineView/utils'
import { CONFIG } from '../../Config/config'
import { fetchAllClientsByChunks } from '../Campaigns/gql'

const DesignManagement = ({ match, history, location }) => {
  const user = authorization.getUser()
  const isAdmin = authorization.isAdmin()
  const clientId = user.client ? Number(user.client.id) : null
  const runSnackBar = useContext(SnackBarContext)

  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [previewDesignData, setPreviewDesignData] = useState(undefined)
  const [duplicatingDesignIds, setDuplicatingDesignIds] = useState(new Set())
  const [paginationInfo, setPaginationInfo] = useState(null)
  const [clientsMap, setClientsMap] = useState({}) // Maps client ID to client name

  const [activeFilters, setActiveFilters] = useState({
    search: '',
    format: 'All',
    status: '0',
    sortBy: 'dateCreated',
    sortDirection: 'desc',
    clientId: null,
  })

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  })

  const {
    designs,
    templates,
    loading,
    createDesign,
    duplicateDesign,
    updateDesign,
    getDesign,
    refreshDesign,
  } = useDesignManagement({
    clientId: isAdmin ? null : clientId, // For admin, don't pass clientId to get all designs
    filters: activeFilters,
    pagination,
    onPaginationChange: setPaginationInfo,
  })

  // Check for savedDesignId parameter when returning from editor
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const savedDesignId = params.get('savedDesignId')

    if (savedDesignId && refreshDesign) {
      // Refresh the specific design to update its preview/thumbnail
      refreshDesign(savedDesignId).then(() => {
        // Clear the URL parameter after refresh
        const newParams = new URLSearchParams(location.search)
        newParams.delete('savedDesignId')
        const newSearch = newParams.toString()
        history.replace({
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
        })
      })
    }
  }, [location.search, refreshDesign, history, location.pathname])

  // Fetch clients for admin users to map client IDs to names
  useEffect(() => {
    const fetchClientsMapping = async () => {
      if (isAdmin) {
        try {
          const { clients } = await fetchAllClientsByChunks({ chunkSize: 100 })
          const mapping = clients.reduce((acc, client) => {
            acc[client.id] = client.name
            return acc
          }, {})
          setClientsMap(mapping)
        } catch (error) {
          console.error('Failed to fetch clients mapping:', error)
        }
      }
    }
    fetchClientsMapping()
  }, [isAdmin])

  const onFilterChange = (key, value) => {
    const newFilters = { ...activeFilters, [key]: value }
    setActiveFilters(newFilters)

    // Reset to first page when filters change
    if (key !== 'sortDirection' && key !== 'sortBy') {
      setPagination(prev => ({ ...prev, page: 1 }))
    }
  }

  const onPageChange = newPage => {
    setPagination(prev => ({ ...prev, page: newPage }))
  }

  const onPageSizeChange = newLimit => {
    setPagination({ page: 1, limit: newLimit })
  }

  const onCreateDesign = async () => {
    const newDesign = await createDesign(
      `Design ${designs.length + 1}`,
      user.id,
    )

    if (isAdmin) {
      // For admin users, open direct campaign builder URL
      const countryCode = user.countryCode || 'US'

      let designUrl = `${CONFIG.SS_BASE_URL}/design/${countryCode}/${
        newDesign.id
      }?variant=integrated&action=create`

      // Add auth parameters for admin - use newDesign's clientId for context
      const linkWithAuth = addAuthParamsToCampaignBuilderUrl(
        designUrl,
        newDesign.clientId,
      )

      // Add cache busting parameters
      const cacheBustParams = new URLSearchParams({
        cacheBust: Date.now().toString(),
        timestamp: Date.now().toString(),
        random: Math.random().toString(),
        session: sessionStorage.getItem('sessionId') || Date.now().toString(),
      })

      const finalUrl = `${linkWithAuth}&${cacheBustParams.toString()}`
      window.open(finalUrl, '_self')
    } else {
      // For non-admin users, navigate in the same tab (iframe page)
      history.push(`${match.path}/design/${newDesign.id}?action=create`)
    }
  }

  const onCreateDesignInFigma = () => {
    window.open(
      'https://www.figma.com/community/plugin/1609266944915750859/oppizi-frames-design-and-launch-high-performing-offline-ads',
      '_blank',
      'noopener,noreferrer',
    )
  }

  const onEditDesign = async id => {
    const design = await getDesign(id)

    if (isAdmin) {
      // For admin users, open direct campaign builder URL
      const format = design.format || 'TEARAWAY'
      const countryCode = user.countryCode || 'US'
      const fileLink = design.s3Link
      const hasHtmlContent = !!design.htmlContent && !!design.htmlContent.length

      let designUrl = `${
        CONFIG.SS_BASE_URL
      }/design/${countryCode}/${id}/${format}/builder?variant=integrated&action=edit&fileLink=${fileLink}&hasHtmlContent=${hasHtmlContent}`

      // Add auth parameters for admin - use design's clientId for context
      const linkWithAuth = addAuthParamsToCampaignBuilderUrl(
        designUrl,
        design.clientId,
      )

      // Add cache busting parameters
      const cacheBustParams = new URLSearchParams({
        cacheBust: Date.now().toString(),
        timestamp: Date.now().toString(),
        random: Math.random().toString(),
        session: sessionStorage.getItem('sessionId') || Date.now().toString(),
      })

      const finalUrl = `${linkWithAuth}&${cacheBustParams.toString()}`
      window.open(finalUrl, '_self')
    } else {
      // For non-admin users, navigate in the same tab (iframe page)
      history.push(
        `${match.path}/design/${id}?action=edit&fileLink=${
          design.s3Link
        }&hasHtmlContent=${!!design.htmlContent &&
          !!design.htmlContent.length}`,
      )
    }
  }

  const onPreviewDesign = async id => {
    const designData = await getDesign(id)
    setPreviewDesignData(designData)
    setPreviewModalOpen(true)
  }

  const onDuplicateDesign = async id => {
    try {
      // Add the design ID to the duplicating set to show loading state
      setDuplicatingDesignIds(prev => new Set([...prev, id]))

      // Show immediate feedback
      runSnackBar({
        type: 'info',
        msg: 'Duplicating design...',
        autoHideDuration: 2000,
      })

      // Duplicate in the background without navigation
      const newDesign = await duplicateDesign(id)

      // Remove from duplicating set
      setDuplicatingDesignIds(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })

      // Show success message
      runSnackBar({
        type: 'success',
        msg: `Design "${newDesign.designName}" created successfully`,
        vertical: 'top',
        horizontal: 'right',
      })
      if (newDesign.htmlContent && newDesign.htmlContent.length > 0) {
        history.push(
          `${match.path}/design/${
            newDesign.id
          }?action=duplicate&baseDocumentId=${id}`,
        )
      }
    } catch (error) {
      // Remove from duplicating set on error
      setDuplicatingDesignIds(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })

      // Show error message
      runSnackBar({
        type: 'error',
        msg: 'Failed to duplicate design. Please try again.',
      })
      console.error('Error duplicating design:', error)
    }
  }

  const onArchiveDesign = async id => {
    await updateDesign({
      designId: id,
      design: {
        status: DESIGN_STATUS.ARCHIVED,
      },
    })
    runSnackBar({
      type: 'success',
      msg: 'Design archived successfully',
    })
  }
  const onUnarchiveDesign = async id => {
    await updateDesign({
      designId: id,
      design: {
        status: DESIGN_STATUS.ACTIVE,
      },
    })
    runSnackBar({
      type: 'success',
      msg: 'Design activated successfully',
    })
  }

  const onDownloadAsPDF = async id => {
    const design = designs.find(design => design.id === id)
    const link = design.s3CmykLink || design.s3Link
    const designName = design.designName || 'design'

    if (link && link !== '' && link.includes('s3')) {
      // Create a download link with proper filename
      try {
        const response = await fetch(link)
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${designName}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        // Fallback to opening in new window if download fails
        console.warn(
          'Failed to download with custom name, opening in new window:',
          error,
        )
        window.open(link, '_blank')
      }
    } else {
      const designData = await getDesign(id)
      openHtmlInNewTabAndPrint(designData.htmlContent, designName)
    }
  }

  const onRenameDesign = async (id, updates) => {
    await updateDesign({
      designId: id,
      design: updates,
    })
    runSnackBar({
      type: 'success',
      msg: 'Design renamed successfully',
    })
  }

  const handleRowClick = id => {
    const design = data.find(d => d.id === id)
    if (design && design.designSource === 'figma') {
      onPreviewDesign(id)
    } else {
      onEditDesign(id)
    }
  }

  const actions = [
    {
      action: onEditDesign,
      label: 'Edit',
      isVisible: design => design.designSource !== 'figma',
    },
    {
      action: onPreviewDesign,
      label: 'Preview',
      isVisible: () => true,
    },
    {
      action: onDuplicateDesign,
      label: 'Duplicate',
      isVisible: () => true,
    },
    {
      action: onRenameDesign,
      label: 'Rename',
      isVisible: () => true,
    },
    {
      action: onDownloadAsPDF,
      label: 'Download as PDF',
      isVisible: () => true,
    },
    {
      action: onArchiveDesign,
      label: 'Archive',
      style: { color: '#E00404' },
      isVisible: design => design.status === DESIGN_STATUS.ACTIVE,
    },
    {
      action: onUnarchiveDesign,
      label: 'Activate',
      style: { color: '#16a34a' },
      isVisible: design => design.status === DESIGN_STATUS.ARCHIVED,
    },
  ]

  const data = activeFilters.status === 'templates' ? templates : designs

  const handleClosePreviewModal = () => {
    setPreviewModalOpen(false)
    setPreviewDesignData(null)
  }

  return (
    <main
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        backgroundColor: '#F9F9FF',
        minHeight: '100vh',
      }}
    >
      <section
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '32px',
          paddingBottom: '10rem',
          width: '100%',
        }}
      >
        <CreativeHeader
          onCreateDesign={onCreateDesign}
          onCreateDesignInFigma={onCreateDesignInFigma}
          type={activeFilters.status === 'templates' ? 'template' : 'design'}
        />
        {activeFilters.status !== 'templates' && (
          <FilterSection
            activeFilters={activeFilters}
            onFilterChange={onFilterChange}
            items={data}
            isAdmin={isAdmin}
          />
        )}
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px',
              fontSize: '16px',
              color: '#6b7280',
            }}
          >
            Loading designs...
          </div>
        ) : (
          <DesignTable
            items={data}
            actions={actions}
            onRowClick={handleRowClick}
            duplicatingDesignIds={duplicatingDesignIds}
            isAdmin={isAdmin}
            clientsMap={clientsMap}
          />
        )}
        {activeFilters.status !== 'templates' && paginationInfo && (
          <Pagination
            paginationInfo={paginationInfo}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        )}
      </section>

      <DesignPreviewModal
        isOpen={previewModalOpen}
        onClose={handleClosePreviewModal}
        designData={previewDesignData}
        designName={
          previewDesignData && previewDesignData.designName
            ? previewDesignData.designName
            : 'Design Preview'
        }
      />
    </main>
  )
}

export default DesignManagement
